import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart as BarChartRecharts, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  Activity, Users, MessageSquare, BookOpen, Wand2, 
  TrendingUp, Clock, RefreshCw, Eye, Calendar, DollarSign, BarChart3, Trash2, Bot, Download
} from 'lucide-react';
import { toast } from 'sonner';

export default function AnalyticsTab({ analytics }) {
  const [usageData, setUsageData] = useState(null);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [selectedDays, setSelectedDays] = useState('30');
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [aiQueries, setAiQueries] = useState([]);
  const [loadingAiQueries, setLoadingAiQueries] = useState(false);
  const [deletingQuery, setDeletingQuery] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('all');

  useEffect(() => {
    fetchUsageData();
    const interval = setInterval(fetchRealtimeStats, 30000);
    return () => clearInterval(interval);
  }, [selectedDays]);

  useEffect(() => {
    if (activeSection === 'ai-queries') {
      fetchAiQueries();
    }
  }, [activeSection]);

  const fetchAiQueries = async () => {
    setLoadingAiQueries(true);
    try {
      const response = await api.get('/admin/ai-queries');
      setAiQueries(response.data);
    } catch (error) {
      console.error('Failed to load AI queries');
      toast.error('Failed to load AI queries');
    } finally {
      setLoadingAiQueries(false);
    }
  };

  const handleDeleteQuery = async (queryId) => {
    if (!window.confirm('Are you sure you want to delete this AI query?')) {
      return;
    }
    setDeletingQuery(queryId);
    try {
      await api.delete(`/admin/ai-queries/${queryId}`);
      setAiQueries(aiQueries.filter(q => q.id !== queryId));
      toast.success('Query deleted successfully');
    } catch (error) {
      toast.error('Failed to delete query');
    } finally {
      setDeletingQuery(null);
    }
  };

  // Generate month options from queries
  const getMonthOptions = () => {
    const months = new Set();
    aiQueries.forEach(q => {
      if (q.created_at) {
        const d = new Date(q.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        months.add(key);
      }
    });
    return Array.from(months).sort().reverse().map(m => {
      const [year, month] = m.split('-');
      const label = new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
      return { value: m, label };
    });
  };

  const filteredQueries = selectedMonth === 'all'
    ? aiQueries
    : aiQueries.filter(q => {
        if (!q.created_at) return false;
        const d = new Date(q.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return key === selectedMonth;
      });

  const handleDeleteAllQueries = async () => {
    if (!window.confirm('Are you sure you want to delete ALL AI queries? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete('/admin/ai-queries');
      setAiQueries([]);
      toast.success('All queries deleted successfully');
    } catch (error) {
      toast.error('Failed to delete queries');
    }
  };

  const fetchUsageData = async () => {
    setLoadingUsage(true);
    try {
      const [summaryRes, realtimeRes] = await Promise.all([
        api.get(`/activity/admin/summary?days=${selectedDays}`),
        api.get('/activity/admin/realtime')
      ]);
      setUsageData(summaryRes.data);
      setRealtimeStats(realtimeRes.data);
    } catch (error) {
      console.error('Failed to load usage data');
    } finally {
      setLoadingUsage(false);
    }
  };

  const fetchRealtimeStats = async () => {
    try {
      const response = await api.get('/activity/admin/realtime');
      setRealtimeStats(response.data);
    } catch (error) {
      console.error('Failed to refresh realtime stats');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'login': return <Users className="w-4 h-4 text-blue-500" />;
      case 'ai_query': return <MessageSquare className="w-4 h-4 text-purple-500" />;
      case 'library_view': return <BookOpen className="w-4 h-4 text-emerald-500" />;
      case 'wizard_use': return <Wand2 className="w-4 h-4 text-orange-500" />;
      case 'zap_tool': return <Activity className="w-4 h-4 text-yellow-500" />;
      default: return <Eye className="w-4 h-4 text-gray-500" />;
    }
  };

  // User distribution data for pie chart
  const statusData = analytics ? [
    { name: 'Active', value: analytics.active_users, color: '#10b981' },
    { name: 'Trial', value: analytics.trial_users, color: '#3b82f6' },
    { name: 'Expired', value: analytics.expired_users, color: '#6b7280' },
    { name: 'Banned', value: analytics.banned_users, color: '#ef4444' }
  ] : [];

  // Activity breakdown for bar chart
  const activityChartData = usageData ? [
    { name: 'Logins', count: usageData.activity_breakdown.logins, fill: '#3b82f6' },
    { name: 'AI Queries', count: usageData.activity_breakdown.ai_queries, fill: '#8b5cf6' },
    { name: 'Library', count: usageData.activity_breakdown.library_views, fill: '#10b981' },
    { name: 'Wizard', count: usageData.activity_breakdown.wizard_uses, fill: '#f97316' },
    { name: 'Zap Tools', count: usageData.activity_breakdown.zap_tool_uses, fill: '#eab308' },
  ] : [];

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Section Tabs */}
      <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <TabsList className="w-full sm:w-auto overflow-x-auto">
            <TabsTrigger value="overview" className="flex items-center gap-1 text-xs sm:text-sm">
              <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex items-center gap-1 text-xs sm:text-sm">
              <Activity className="w-3 h-3 sm:w-4 sm:h-4" />
              Usage
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1 text-xs sm:text-sm">
              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="ai-queries" className="flex items-center gap-1 text-xs sm:text-sm">
              <Bot className="w-3 h-3 sm:w-4 sm:h-4" />
              AI Queries
            </TabsTrigger>
          </TabsList>
          
          {activeSection !== 'overview' && (
            <div className="flex items-center gap-2">
              <Select value={selectedDays} onValueChange={setSelectedDays}>
                <SelectTrigger className="w-[130px]">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchUsageData} title="Refresh">
                <RefreshCw className={`w-4 h-4 ${loadingUsage ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          )}
        </div>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6">
          {analytics && (
            <>
              {/* Real-time Stats Row */}
              {realtimeStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-blue-600 font-medium">Last 24 Hours</p>
                          <p className="text-2xl font-bold text-blue-700">{realtimeStats.activities_24h}</p>
                          <p className="text-xs text-blue-500">activities</p>
                        </div>
                        <Clock className="w-8 h-8 text-blue-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-purple-600 font-medium">Active Users (24h)</p>
                          <p className="text-2xl font-bold text-purple-700">{realtimeStats.unique_users_24h}</p>
                          <p className="text-xs text-purple-500">unique users</p>
                        </div>
                        <Users className="w-8 h-8 text-purple-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-emerald-600 font-medium">AI Queries (24h)</p>
                          <p className="text-2xl font-bold text-emerald-700">{realtimeStats.ai_queries_24h}</p>
                          <p className="text-xs text-emerald-500">questions asked</p>
                        </div>
                        <MessageSquare className="w-8 h-8 text-emerald-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-orange-600 font-medium">Total Revenue</p>
                          <p className="text-2xl font-bold text-orange-700">₱{analytics.total_revenue.toLocaleString()}</p>
                          <p className="text-xs text-orange-500">all time</p>
                        </div>
                        <DollarSign className="w-8 h-8 text-orange-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Distribution Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>User Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={statusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {statusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Key Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle>Key Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Conversion Rate</span>
                      <span className="text-xl font-bold text-blue-600">{analytics.conversion_rate}%</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Upcoming Expirations</span>
                      <span className="text-xl font-bold text-amber-600">{analytics.upcoming_expirations}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Recent Registrations</span>
                      <span className="text-xl font-bold text-purple-600">{analytics.recent_registrations}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Total Users</span>
                      <span className="text-xl font-bold text-gray-700">{analytics.total_users}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* APP USAGE TAB */}
        <TabsContent value="usage" className="space-y-6">
          {usageData && (
            <>
              {/* Activity Breakdown */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Activity Breakdown ({selectedDays} days)</CardTitle>
                  <CardDescription>
                    {usageData.total_activities} total activities from {usageData.unique_active_users} active users
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <Users className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-blue-700">{usageData.activity_breakdown.logins}</p>
                      <p className="text-xs text-blue-600">Logins</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-purple-700">{usageData.activity_breakdown.ai_queries}</p>
                      <p className="text-xs text-purple-600">AI Queries</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <BookOpen className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-emerald-700">{usageData.activity_breakdown.library_views}</p>
                      <p className="text-xs text-emerald-600">Library Views</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <Wand2 className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-orange-700">{usageData.activity_breakdown.wizard_uses}</p>
                      <p className="text-xs text-orange-600">Wizard Uses</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <Activity className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-yellow-700">{usageData.activity_breakdown.zap_tool_uses}</p>
                      <p className="text-xs text-yellow-600">Zap Tools</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <Eye className="w-5 h-5 text-gray-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-gray-700">{usageData.activity_breakdown.page_views}</p>
                      <p className="text-xs text-gray-600">Page Views</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activity Bar Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Activity by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChartRecharts data={activityChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {activityChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChartRecharts>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Daily Trend */}
                {usageData.daily_trend && usageData.daily_trend.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Daily Activity Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={usageData.daily_trend.slice(-30)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="_id" 
                            tick={{ fontSize: 10 }}
                            tickFormatter={(val) => formatDate(val)}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip labelFormatter={(val) => formatDate(val)} />
                          <Area 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#10b981" 
                            fill="#10b98133"
                            name="Activities"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Feature Usage */}
              {usageData.feature_usage && usageData.feature_usage.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Feature Usage</CardTitle>
                    <CardDescription>Which features are being used most</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {usageData.feature_usage.map((feature) => (
                        <Badge key={feature.feature} variant="outline" className="text-sm py-1 px-3">
                          {feature.feature}: <span className="font-bold ml-1">{feature.count}</span>
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
          
          {loadingUsage && !usageData && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-emerald-600 mr-2" />
              <span>Loading usage data...</span>
            </div>
          )}
        </TabsContent>

        {/* USER ACTIVITY TAB */}
        <TabsContent value="users" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Users */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Most Active Users</CardTitle>
                <CardDescription>Top 20 users by activity ({selectedDays} days)</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {usageData?.top_users && usageData.top_users.map((user, idx) => (
                      <div
                        key={user.user_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm">
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900 truncate max-w-[160px]">{user.email}</p>
                            <p className="text-xs text-gray-500">{user.total_activities} activities</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs px-2">
                            <Users className="w-3 h-3 mr-1" />
                            {user.logins}
                          </Badge>
                          <Badge variant="outline" className="text-xs px-2">
                            <MessageSquare className="w-3 h-3 mr-1" />
                            {user.ai_queries}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {(!usageData?.top_users || usageData.top_users.length === 0) && (
                      <p className="text-center text-gray-500 py-4">No user activity data yet</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Activity</CardTitle>
                <CardDescription>Latest user interactions</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {realtimeStats?.recent_activities && realtimeStats.recent_activities.map((activity, idx) => (
                      <div key={`${activity.user_email}-${activity.timestamp || idx}`} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        {getActivityIcon(activity.activity_type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{activity.user_email}</p>
                          <p className="text-xs text-gray-600 capitalize">{activity.activity_type.replace('_', ' ')}</p>
                          {activity.activity_detail && (
                            <p className="text-xs text-gray-400 truncate">{activity.activity_detail}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatTime(activity.created_at)}
                        </span>
                      </div>
                    ))}
                    {(!realtimeStats?.recent_activities || realtimeStats.recent_activities.length === 0) && (
                      <p className="text-center text-gray-500 py-4">No recent activity</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI Queries Tab */}
        <TabsContent value="ai-queries" className="mt-6">
          <Card>
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Bot className="w-5 h-5 text-purple-500" />
                    AI Query History
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    All AI queries made by users ({filteredQueries.length}{selectedMonth !== 'all' ? ` of ${aiQueries.length}` : ''} total)
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-[140px] sm:w-[180px]" data-testid="ai-queries-month-filter">
                      <Calendar className="w-4 h-4 mr-1" />
                      <SelectValue placeholder="Filter by month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {getMonthOptions().map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchAiQueries}
                    disabled={loadingAiQueries}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingAiQueries ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline ml-2">Refresh</span>
                  </Button>
                  {aiQueries.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteAllQueries}
                      data-testid="delete-all-ai-queries-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline ml-2">Delete All</span>
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {loadingAiQueries ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : filteredQueries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bot className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>{selectedMonth !== 'all' ? 'No AI queries for this month' : 'No AI queries recorded yet'}</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {filteredQueries.map((query) => (
                      <div key={query.id} className="p-3 sm:p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
                              <Badge variant="secondary" className="text-xs truncate max-w-[150px] sm:max-w-none">
                                {query.user_email || 'Unknown User'}
                              </Badge>
                              <span className="text-xs text-gray-400">
                                {new Date(query.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="mb-2">
                              <p className="text-sm font-medium text-gray-700 mb-1">Question:</p>
                              <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                                {query.question}
                              </p>
                            </div>
                            {query.response && (
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-1">Response:</p>
                                <p className="text-sm text-gray-600 bg-white p-2 rounded border max-h-48 overflow-y-auto whitespace-pre-wrap">
                                  {query.response}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                const text = `Question: ${query.question}\n\nAnswer: ${query.response || 'No response'}\n\nUser: ${query.user_email || 'Unknown'}\nDate: ${new Date(query.created_at).toLocaleString()}`;
                                const blob = new Blob([text], { type: 'text/plain' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `AI_QA_${new Date(query.created_at).toISOString().split('T')[0]}.txt`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                                toast.success('Q&A downloaded');
                              }}
                              data-testid={`download-query-${query.id}`}
                              title="Download Q&A"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteQuery(query.id)}
                              disabled={deletingQuery === query.id}
                              data-testid={`delete-query-${query.id}`}
                              title="Delete"
                            >
                              {deletingQuery === query.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
